/* eslint-disable react/no-unknown-property */

import { ActiveDesignSlice } from '@/stores/active-design'
import ConcreteSlab3D from './ConcreteSlab3D'
import { useEffect, useMemo, useState } from 'react'
import { useBounds } from '@react-three/drei'
import { Euler, MathUtils, Vector3 } from 'three'
import useChannelFixingPosition from '@/hooks/useChannelFixingPosition'
import TBoltModel from './components/TBoltModel'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/stores'
import useComponentProduct from '@/hooks/useComponentProduct'
import { useFrame } from '@react-three/fiber'
import { setPreviousCameraAlignmentKey } from '@/stores/camera-control-type'
import { PerspectiveCamera as PerspectiveCameraType } from 'three'
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'
import CastInChannelContainer from './components/CastInChannelContainer'
import useDownloadModel from '@/hooks/useDownloadModel'

// Needed to define a buffer as the camera position when transitioning to a new position
// can sometimes not reach the exact target
const cameraResetBuffer = 20

// For larger models, the buffer doesn't always work, so as a fallback we set a timeout
const cameraResetFallbackTimeout = 600

// Speed at which the camera moves to the target position
const cameraResetSpeed = 10

// Default field of view for the perspective camera
const defaultFov = 50

type Design3DProps = {
  design: ActiveDesignSlice['design']
  cameraRef?: React.MutableRefObject<PerspectiveCameraType | null>
  orbitRef?: React.MutableRefObject<OrbitControlsImpl | null>
}

const Design3D = ({ design, cameraRef, orbitRef }: Design3DProps) => {
  const bounds = useBounds()

  const dispatch = useDispatch()

  // Used for identifying the orientation of the model
  const { fixingPosition } = useChannelFixingPosition()

  // Used to determine if the camera should perform an initial reset
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  // Needed for camera reset. We use a unique key to identify when the camera should reset
  const currentCameraAlignmentKey = useSelector((state: RootState) => state.selectedCameraControlType.cameraAlignmentKey)
  const previousCameraAlignmentKey = useSelector((state: RootState) => state.selectedCameraControlType.previousCameraAlignmentKey)

  // Camera target position is used to move the camera to a specific position
  const [cameraTargetPosition, setCameraTargetPosition] = useState<Vector3 | null>(null)

  const { registerDownloadModelListener, unregisterDownloadModelListener } = useDownloadModel()

  useEffect(() => {
    registerDownloadModelListener()
    return () => {
      unregisterDownloadModelListener()
    }
  }, [registerDownloadModelListener, unregisterDownloadModelListener])

  // Design configuration, for identifying bounding sphere dimensions
  const slab = design?.components.find(c => c.type === 'concrete_slab')
  const channel = design?.components.find(c => slab ? c.parent_id === slab.id : false)
  const { productVariant } = useComponentProduct({ component: channel })

  // Calculate the bounding sphere radius from the longest edge of the model
  const boundingSphereRadius = useMemo(() => {
    if (!slab) {
      return 0
    }

    const details = slab.concrete_slab!
    const channelLength = channel?.cast_in_anchor_channel?.custom_length ?? channel?.cast_in_anchor_channel?.length ?? 0

    const xEdgeLength = details.edge_distance_x_neg + details.edge_distance_x_pos
    // Add the width of the channel to the Y dimension so it's considered in the overall size of the slab
    const yEdgeLength = details.edge_distance_y_neg + details.edge_distance_y_pos + (productVariant?.cast_in_anchor_channel?.width ?? 0)

    // Get the longest edge of the slab, including the channel
    const longestEdge = Math.max((xEdgeLength + channelLength), yEdgeLength, details.thickness)

    // Half it as this is a radius
    return (longestEdge / 2)
  }, [slab, channel, productVariant])

  // Calculated courtesy of https://discourse.threejs.org/t/how-can-i-place-camera-to-show-all-objects/35226/7
  const cameraDistance = useMemo(() => {
    const fov = cameraRef?.current?.fov ?? defaultFov

    const distance = boundingSphereRadius / Math.sin(fov / 2)

    return Math.abs(distance / 4)
  }, [boundingSphereRadius, cameraRef])

  // Generate the 3D design components
  const designs = useMemo(() => {
    return design?.components.map(component => {
      if (component.type === 'concrete_slab') {
        return (
          <ConcreteSlab3D
            key={component.id}
            design={design}
            component={component!}
          />
        )
      } else if (component.type === 'product_variant') {
        if (Object.prototype.hasOwnProperty.call(component, 'cast_in_anchor_channel')) {
          return (
            <CastInChannelContainer
              key={component.id}
              component={component!}
            />
          )
        }
        if (Object.prototype.hasOwnProperty.call(component, 't_bolt')) {
          return (
            <TBoltModel
              key={component.id}
              component={component!}
            />
          )
        }
      }
      return null
    }).filter((design) => design !== null)
  }, [design])

  // Reset the camera position when the camera alignment key changes or on the first load
  useEffect(() => {
    if (!isFirstLoad && (currentCameraAlignmentKey === previousCameraAlignmentKey)) {
      return
    }

    dispatch(setPreviousCameraAlignmentKey(currentCameraAlignmentKey))

    setCameraTargetPosition(new Vector3(cameraDistance, cameraDistance, cameraDistance))

    // For larger models, the buffer doesn't always work, so as a fallback we set a timeout
    setTimeout(() => {
      setCameraTargetPosition(null)
    }, cameraResetFallbackTimeout)

    setIsFirstLoad(false)
  }, [cameraDistance, isFirstLoad, currentCameraAlignmentKey, previousCameraAlignmentKey, dispatch])

  // Move the camera position to the target position on a per-frame basis so it smoothly animates
  useFrame((_, delta) => {
    if (!cameraRef?.current || !orbitRef?.current || !cameraTargetPosition) {
      return
    }

    if (cameraTargetPosition.round().distanceTo(cameraRef.current.position.round()) < cameraResetBuffer) {
      setCameraTargetPosition(null)
      return
    }

    // Annoyingly, we still want bounds to figure out the clipping planes
    // but we want to fit the camera position manually to avoid frequent misalignments as
    // has previously happened
    bounds.clip()

    // Damp interpolates between two positions. Takes the delta time (Time between frames) into
    // account to ensure smooth frame-rate independent movement
    cameraRef.current.position.x = MathUtils.damp(
      cameraRef.current.position.x,
      cameraTargetPosition.x,
      cameraResetSpeed,
      delta,
    )

    cameraRef.current.position.y = MathUtils.damp(
      cameraRef.current.position.y,
      cameraTargetPosition.y,
      cameraResetSpeed,
      delta,
    )

    cameraRef.current.position.z = MathUtils.damp(
      cameraRef.current.position.z,
      cameraTargetPosition.z,
      cameraResetSpeed,
      delta,
    )

    orbitRef.current.target.x = MathUtils.damp(
      orbitRef.current.target.x,
      0,
      cameraResetSpeed,
      delta,
    )

    orbitRef.current.target.y = MathUtils.damp(
      orbitRef.current.target.y,
      0,
      cameraResetSpeed,
      delta,
    )

    orbitRef.current.target.z = MathUtils.damp(
      orbitRef.current.target.z,
      0,
      cameraResetSpeed,
      delta,
    )
  })

  return (
    <group rotation={fixingPosition === 'face' ? new Euler(Math.PI / 2) : new Euler(0)}>
      { designs }
    </group>
  )
}

export default Design3D
